import { observable, action, computed } from "mobx";
import Uppy from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";
import Api from "~/service/Api";
import JobStatus from "~/constants/PhotoEditing/JobStatus";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";

class UploadStore {
  /**
   * a simple copy of images tracked by uppy.
   * It's mainly used as a counter.
   *
   * @property {Array}
   */
  @observable photos = [];

  /**
   * @property {Object|null}
   */
  @observable photoEditingJob = null;

  /**
   * @property {number|null}
   */
  @observable photoEditingImageId = null;

  /**
   * @property {boolean}
   */
  @observable isLoading = false;

  /**
   * @property {boolean}
   */
  @observable isUploading = false;

  /**
   * @property {boolean}
   */
  @observable isCompleted = false;

  /**
   * @property {boolean}
   */
  @observable hasUploadErrors = false;

  /**
   * @property {number|null}
   */
  photoEditingJobId = null;

  /**
   * @property {string}
   */
  reviewImagesUrl = "";

  uploader = null;

  // a helper for integrating with PromiseButton,
  // for storing the resolve function for the returned promise in upload() method
  uploadPromiseResolver = null;

  @computed
  get redirectTo() {
    // Redirect to view job if the job is not in the process of creation any more
    if (this.photoEditingJob && this.photoEditingJob.status !== JobStatus.IN_CREATION) {
      return `/${this.photoEditingJobId}`;
    }

    return null;
  }

  /**
   *
   * @param {int} photoEditingJobId used for fetching the corresponding job (see load() method)
   * @param {photoEditingJobId} photoEditingJobId. Optionally a full PhotoEditingJob object can be passed so we can
   *        avoid fetching the job. For now we support both ways, but we'll probably end just passing and requiring
   *        a full job object.
   *
   */
  constructor(photoEditingJobId) {
    // if photoEditingJob was given, then let's ignore the photoEditingJobId
    if (!photoEditingJobId) {
      throw new Error("Expected a photoEditingJobId");
    }

    this.photoEditingJobId = photoEditingJobId;

    this.reviewImagesUrl = `/${this.photoEditingJobId}/images`;
  }

  @computed
  get canUpload() {
    // if there's an upload in progress then do not allow clicking the button again
    if (this.isUploading) {
      return false;
    }

    return this.photos.length > 0;
  }

  @computed
  get canGoToReviewImagesPage() {
    // if we are in the middle of an api request we should not allow moving away from this page
    if (this.isLoading) {
      return false;
    }

    if (this.isUploading) {
      return false;
    }

    // if the job already got images then we can go to the review images page.
    if (this.jobHasPhotos()) {
      return true;
    }

    return this.isCompleted && !this.hasUploadErrors;
  }

  jobHasPhotos() {
    // there must be a job, and contains images, and images should have photos.
    if (!this.photoEditingJob) {
      return false;
    }

    if (this.photoEditingJob.photoEditingImages.length === 0) {
      return false;
    }

    const firstImage = this.photoEditingJob.photoEditingImages[0];

    return firstImage.photoEditingPhotos.length > 0;
  }

  @action
  load() {
    this.isLoading = true;
    // init the uploader
    this.initUploader();

    return Api.get(`/photo-editing/jobs/${this.photoEditingJobId}`)
      .then(response => {
        this.photoEditingJob = new PhotoEditingJob(response.data.photoEditingJob);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  initUploader() {
    // configure uppy
    this.uploader = Uppy({
      allowMultipleUploads: true,
      restrictions: { allowedFileTypes: [".jpg", ".jpeg"] },
    });

    // configure HRUpload plugin

    this.uploader.use(XHRUpload, {
      endpoint: "/api/photo-editing/photos",
      fieldName: "uploadedFile",
      // try with 10 on prod/staging
      limit: 3,
      // Post processing can be slow and we may need more than 30s in some cases
      timeout: 240 * 1000,
      getUploadParameters: file => {
        return new Promise((resolve, reject) => {
          this.createUploadItem(this.uploadArea, file, resolve, reject);
        });
      },
    });

    // Register uppy event handlers

    // file added
    this.uploader.on("file-added", this.onFileAdded.bind(this));

    // a file has been removed
    this.uploader.on("file-removed", this.onFileRemoved.bind(this));

    // all files uploaded
    this.uploader.on("complete", this.onComplete.bind(this));

    // when cancelling all photos
    this.uploader.on("cancel-all", this.onCancelAll.bind(this));
  }

  @action
  onFileAdded(file) {
    this.photos.push(file);
  }

  @action
  onFileRemoved(file) {
    const indexAt = this.photos.indexOf(file);
    this.photos.splice(indexAt, 1);
  }

  @action
  onComplete(result) {
    this.isUploading = false;
    // complete event is fired even if some or all individual file uploads failed
    this.isCompleted = true;

    if (this.uploadPromiseResolver) {
      this.uploadPromiseResolver();
      this.uploadPromiseResolver = null;
    }

    if (result.failed && result.failed.length > 0) {
      this.hasUploadErrors = true;
    } else {
      // all uploads where successful
      this.photos.clear();
      this.hasUploadErrors = false;
    }
  }

  @action
  onCancelAll() {
    this.isUploading = false;
    this.isCompleted = false;
    this.hasUploadErrors = false;
    this.photos.clear();
  }

  @action
  async upload() {
    // caching value here so we can reset hasUploadErrors first, then to actually upload/retry last.
    this.isUploading = true;
    this.isCompleted = false;

    let photoEditingImageId;

    if (this.photoEditingImageId) {
      photoEditingImageId = this.photoEditingImageId;
    } else {
      photoEditingImageId = await this.createPhotoEditingImage();
      this.photoEditingImageId = photoEditingImageId;
    }

    // wait until Uppy notifies 'complete' (even if with errors).
    await this.doUpload(photoEditingImageId);

    // if no upload errors, then proceed to sort images by date in the backend.
    if (!this.hasUploadErrors) {
      await this.sortPhotos();
    }

    // consistent return
    return Promise.resolve();
  }

  @action
  doUpload(photoEditingImageId) {
    // add the image id to the metadata of each photo.
    this.photos.forEach(file => {
      this.uploader.setFileMeta(file.id, { photoEditingImageId });
    });

    // If there were upload errors, let's retry all failed ones.
    if (this.hasUploadErrors) {
      this.uploader.retryAll();
    } else {
      this.uploader.upload();
    }

    // Return a promise here that will only be fulfilled in the "complete" uppy event handler.
    // This is needed so the PromiseButton keeps spinning while we upload all photos.
    return new Promise(resolve => {
      this.uploadPromiseResolver = resolve;
    });
  }

  @action
  closeUploader() {
    this.uploader.close();
  }

  @action
  createPhotoEditingImage() {
    if (this.photoEditingImageId) {
      return Promise.resolve(this.photoEditingImageId);
    }

    this.isLoading = true;

    return Api.post("/photo-editing/images", {
      photoEditingJobId: this.photoEditingJobId,
    }).then(response => {
      this.isLoading = false;

      return response.data.photoEditingImage.photoEditingImageId;
    });
  }

  @action
  sortPhotos() {
    this.isLoading = true;
    return Api.post(`/photo-editing/images/${this.photoEditingImageId}/sort-photos`).then(() => {
      this.isLoading = false;
    });
  }
}

export default UploadStore;
